import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null); // { code, message }

  const checkAuth = useCallback(async () => {
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
      setAuthError(null);
    } catch (e) {
      setUser(null);
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "INVITE_REQUIRED") {
        setAuthError(detail);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      console.error("Logout request failed (clearing session anyway)", e);
    }
    setUser(null);
    window.location.href = "/login";
  };

  // Granular permission check. Owner implicitly holds everything.
  const can = useCallback((key) => {
    if (!user) return false;
    if (user.role === "owner") return true;
    return !!(user.permissions && user.permissions[key]);
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, setUser, loading, checkAuth, logout, authError, setAuthError, can }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
